import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { UserPlus, UserMinus, CheckCircle, Circle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OnboardingPage = () => {
  const { isHR } = useAuth();
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChecklists();
  }, []);

  const fetchChecklists = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/checklists`, { withCredentials: true });
      setChecklists(response.data);
    } catch (error) {
      console.error('Fetch checklists error:', error);
      toast.error('Failed to load checklists');
    } finally {
      setLoading(false);
    }
  };

  const onboardingItems = checklists.filter(c => c.type === 'onboarding');
  const offboardingItems = checklists.filter(c => c.type === 'offboarding');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const ChecklistSection = ({ items, type }) => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {type === 'onboarding' ? <UserPlus className="w-5 h-5 text-[hsl(var(--success))]" /> : <UserMinus className="w-5 h-5 text-[hsl(var(--warning))]" />}
          {type === 'onboarding' ? 'Onboarding' : 'Offboarding'} Checklist Template
        </CardTitle>
      </CardHeader>
      <CardContent>
        {items.length > 0 ? (
          <div className="space-y-3">
            {items.map((item, index) => (
              <div key={item.id} className="flex items-start gap-3 p-3 bg-muted rounded-md">
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <p className="font-medium">{item.title}</p>
                  {item.description && (
                    <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                  )}
                  {item.due_days !== undefined && (
                    <Badge variant="outline" className="mt-2 text-xs">
                      {item.due_days === 0 ? 'Day of joining/exit' : 
                       item.due_days > 0 ? `Within ${item.due_days} days` : 
                       `${Math.abs(item.due_days)} days before`}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-muted-foreground py-8">
            No checklist items found. {isHR && 'Add items to create a template.'}
          </p>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="onboarding-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <UserPlus className="w-7 h-7 text-primary" />
          Onboarding & Offboarding
        </h1>
        <p className="text-muted-foreground">Manage new hire and exit checklists</p>
      </div>

      <Tabs defaultValue="onboarding">
        <TabsList>
          <TabsTrigger value="onboarding" className="gap-2">
            <UserPlus className="w-4 h-4" />
            Onboarding
          </TabsTrigger>
          <TabsTrigger value="offboarding" className="gap-2">
            <UserMinus className="w-4 h-4" />
            Offboarding
          </TabsTrigger>
        </TabsList>

        <TabsContent value="onboarding" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChecklistSection items={onboardingItems} type="onboarding" />
            
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Onboarding Best Practices</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { title: 'Pre-Day 1', items: ['Send welcome email', 'Prepare workstation', 'Set up accounts'] },
                  { title: 'Day 1', items: ['HR orientation', 'IT setup', 'Team introduction'] },
                  { title: 'Week 1', items: ['Meet with manager', 'Review OKRs', 'Start training'] },
                  { title: 'Month 1', items: ['Complete security training', '30-day check-in', 'Feedback session'] },
                ].map((phase, i) => (
                  <div key={i}>
                    <p className="font-medium text-sm">{phase.title}</p>
                    <ul className="mt-1 space-y-1">
                      {phase.items.map((item, j) => (
                        <li key={j} className="text-sm text-muted-foreground flex items-center gap-2">
                          <Circle className="w-2 h-2 text-primary" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="offboarding" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChecklistSection items={offboardingItems} type="offboarding" />
            
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Offboarding Best Practices</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { title: 'Notice Period', items: ['Document handover', 'Knowledge transfer', 'Train replacement'] },
                  { title: 'Last Week', items: ['Return assets', 'Exit interview', 'Clear dues'] },
                  { title: 'Last Day', items: ['Revoke access', 'Farewell', 'Final settlement'] },
                  { title: 'Post Exit', items: ['Experience letter', 'Full & final', 'Alumni connect'] },
                ].map((phase, i) => (
                  <div key={i}>
                    <p className="font-medium text-sm">{phase.title}</p>
                    <ul className="mt-1 space-y-1">
                      {phase.items.map((item, j) => (
                        <li key={j} className="text-sm text-muted-foreground flex items-center gap-2">
                          <Circle className="w-2 h-2 text-[hsl(var(--warning))]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default OnboardingPage;
